import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  payCardsApi,
  rateCardsApi,
  zonesApi,
  vehicleTypesApi,
  type PayCell,
  type PayCardImportPreview,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal, ConfirmModal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { formatPeso, humanize } from '@/lib/format';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

const CSV_HEADER = 'originZoneCode,destinationZoneCode,vehicleTypeCode,driverAmount,helperAmount,extraStopAmount';

/** Template rows for every zone pair × vehicle type you actually have (reverse lanes are mirrored). */
function buildCsvTemplate(zoneCodes: string[], vehicleCodes: string[]): string {
  if (!zoneCodes.length || !vehicleCodes.length) {
    return [CSV_HEADER, 'Z1,Z2,6W,750.00,375.00,0', 'Z1,Z2,10W,1500.00,750.00,0'].join('\n');
  }
  const rows: string[] = [CSV_HEADER];
  zoneCodes.forEach((origin, i) => {
    zoneCodes.slice(i).forEach((destination) => {
      vehicleCodes.forEach((vt) => rows.push(`${origin},${destination},${vt},,,0`));
    });
  });
  return rows.join('\n');
}

function money(n: string | number) {
  return formatPeso(n);
}

export function AdminPayCardDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data: card, loading, reload } = useAsync(() => payCardsApi.get(id), [id]);
  const { data: cells, reload: reloadCells } = useAsync(() => payCardsApi.listCells(id), [id]);
  const { data: zones } = useAsync(() => zonesApi.list(), []);
  const { data: vehicleTypes } = useAsync(() => vehicleTypesApi.list(), []);
  const { data: rateCards } = useAsync(() => rateCardsApi.list(), []);

  const zoneMap = useMemo(
    () => Object.fromEntries((zones ?? []).map((z) => [z.id, `${z.code} · ${z.name}`])),
    [zones],
  );
  const vtMap = useMemo(
    () => Object.fromEntries((vehicleTypes ?? []).map((v) => [v.id, `${v.code} · ${v.name}`])),
    [vehicleTypes],
  );

  const [editMeta, setEditMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    name: '',
    pairedRateCardId: '',
    threshold: '',
    basis: '6w' as 'own' | '6w',
    driverMnl: '',
    driverCrk: '',
    helperMnl: '',
    helperCrk: '',
  });
  const [cellModal, setCellModal] = useState(false);
  const [editingCell, setEditingCell] = useState<PayCell | null>(null);
  const [cellForm, setCellForm] = useState({
    originZoneId: '',
    destinationZoneId: '',
    vehicleTypeId: '',
    driverAmount: '',
    helperAmount: '',
    extraStopAmount: '0',
  });
  const [confirmDeleteCard, setConfirmDeleteCard] = useState(false);
  const [confirmDeleteCell, setConfirmDeleteCell] = useState<PayCell | null>(null);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();
  const toast = useToast();

  const [csvText, setCsvText] = useState('');
  const [importPreview, setImportPreview] = useState<PayCardImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const { flash: importFlash, ok: importOk, error: importError, clear: importClear } = useFlash();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openEditMeta = () => {
    if (!card) return;
    const defaults = card.classificationConfig?.default;
    setMetaForm({
      name: card.name,
      pairedRateCardId: card.pairedRateCardId ?? '',
      threshold: defaults?.threshold ?? '',
      basis: defaults?.basis ?? '6w',
      driverMnl: defaults?.driverMnl ?? '',
      driverCrk: defaults?.driverCrk ?? '',
      helperMnl: defaults?.helperMnl ?? '',
      helperCrk: defaults?.helperCrk ?? '',
    });
    setEditMeta(true);
  };

  const saveMeta = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clearFlash();
    try {
      await payCardsApi.update(id, {
        name: metaForm.name,
        pairedRateCardId: metaForm.pairedRateCardId || null,
        classificationConfig: {
          default: {
            threshold: metaForm.threshold,
            basis: metaForm.basis,
            driverMnl: metaForm.driverMnl,
            driverCrk: metaForm.driverCrk,
            helperMnl: metaForm.helperMnl,
            helperCrk: metaForm.helperCrk,
          },
        },
      });
      setEditMeta(false);
      toast('Pay card details saved');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not save the pay card details.'));
    } finally {
      setBusy(false);
    }
  };

  const openAddCell = () => {
    setEditingCell(null);
    setCellForm({
      originZoneId: zones?.[0]?.id ?? '',
      destinationZoneId: zones?.[1]?.id ?? zones?.[0]?.id ?? '',
      vehicleTypeId: vehicleTypes?.[0]?.id ?? '',
      driverAmount: '',
      helperAmount: '',
      extraStopAmount: '0',
    });
    setCellModal(true);
  };

  const openEditCell = (cell: PayCell) => {
    setEditingCell(cell);
    setCellForm({
      originZoneId: cell.originZoneId,
      destinationZoneId: cell.destinationZoneId,
      vehicleTypeId: cell.vehicleTypeId,
      driverAmount: String(cell.driverAmount),
      helperAmount: String(cell.helperAmount),
      extraStopAmount: String(cell.extraStopAmount ?? '0'),
    });
    setCellModal(true);
  };

  const saveCell = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    clearFlash();
    try {
      await payCardsApi.upsertCells(id, [
        {
          originZoneId: cellForm.originZoneId,
          destinationZoneId: cellForm.destinationZoneId,
          vehicleTypeId: cellForm.vehicleTypeId,
          driverAmount: cellForm.driverAmount,
          helperAmount: cellForm.helperAmount,
          extraStopAmount: cellForm.extraStopAmount || '0',
        },
      ]);
      setCellModal(false);
      toast(
        cellForm.originZoneId !== cellForm.destinationZoneId
          ? 'Pay lane saved — the return direction was set to the same pay'
          : 'Pay lane saved',
      );
      reloadCells();
    } catch (err) {
      showError(errorText(err, 'Could not save the pay lane.'));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    clearFlash();
    try {
      await payCardsApi.activate(id);
      setConfirmActivate(false);
      showOk('Pay card activated — crew pay for new trips will use it.');
      toast(`${card?.name ?? 'Pay card'} is now active`);
      reload();
    } catch (err) {
      setConfirmActivate(false);
      showError(errorText(err, 'Could not activate the pay card.'));
    } finally {
      setBusy(false);
    }
  };

  const deleteCard = async () => {
    setBusy(true);
    try {
      await payCardsApi.remove(id);
      navigate('/app/admin/pay-cards');
    } catch (err) {
      showError(errorText(err, 'Could not delete the pay card.'));
      setConfirmDeleteCard(false);
    } finally {
      setBusy(false);
    }
  };

  const deleteCell = async () => {
    if (!confirmDeleteCell) return;
    setBusy(true);
    try {
      await payCardsApi.deleteCell(id, confirmDeleteCell.id);
      setConfirmDeleteCell(null);
      toast('Pay lane deleted');
      reloadCells();
    } catch (err) {
      setConfirmDeleteCell(null);
      showError(errorText(err, 'Could not delete the pay lane.'));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const template = buildCsvTemplate(
      (zones ?? []).map((z) => z.code),
      (vehicleTypes ?? []).filter((v) => v.status === 'active').map((v) => v.code),
    );
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pay-card-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFileChosen = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
    setImportPreview(null);
    importClear();
  };

  const runImportPreview = async () => {
    if (!csvText.trim()) {
      importError('Upload or paste CSV content first');
      return;
    }
    setImporting(true);
    importClear();
    try {
      const preview = await payCardsApi.previewImport(id, csvText);
      setImportPreview(preview);
    } catch (err) {
      importError(errorText(err, 'Could not check the file.'));
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    importClear();
    try {
      const res = await payCardsApi.import(id, csvText);
      importOk(`Imported ${res.imported} pay lane${res.imported === 1 ? '' : 's'}`);
      toast(`Imported ${res.imported} pay lane${res.imported === 1 ? '' : 's'}`);
      setImportPreview(null);
      setCsvText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      reloadCells();
    } catch (err) {
      importError(errorText(err, 'Could not import the pay lanes.'));
    } finally {
      setImporting(false);
    }
  };

  if (loading && !card) {
    return (
      <Layout navItems={adminNavItems}>
        <div className="page"><div className="loading-center"><div className="spinner" /></div></div>
      </Layout>
    );
  }

  if (!card) {
    return (
      <Layout navItems={adminNavItems}>
        <div className="page">
          <p>Pay card not found.</p>
          <Link to="/app/admin/pay-cards">Back to pay cards</Link>
        </div>
      </Layout>
    );
  }

  const pairedName = rateCards?.find((r) => r.id === card.pairedRateCardId)?.name;
  const defaults = card.classificationConfig?.default;

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <p className="back-link">
          <Link to="/app/admin/pay-cards">← Pay cards</Link>
        </p>

        <div className="page-header row-between">
          <div>
            <p className="kicker">Pay card</p>
            <h1>{card.name}</h1>
            <div className="meta-row">
              <StatusBadge status={card.status} size="sm" />
              <span className="text-muted">v{card.version ?? 1}</span>
              {pairedName && <span className="text-muted">Paired · {pairedName}</span>}
            </div>
          </div>
          <div className="actions-row">
            <Button variant="secondary" size="sm" onClick={openEditMeta}>Edit details</Button>
            {card.status !== 'active' && (
              <Button size="sm" onClick={() => setConfirmActivate(true)}>Activate</Button>
            )}
            {card.status !== 'active' && (
              <Button variant="danger" size="sm" onClick={() => setConfirmDeleteCard(true)}>Delete</Button>
            )}
          </div>
        </div>

        <FlashMessage flash={flash} />

        {defaults && (
          <section className="card-section classification">
            <h2>Default pay</h2>
            <p className="text-muted">
              Used when a lane has no pay set below. Trips priced under {money(defaults.threshold)} pay
              the Metro Manila rate; trips at or above it pay the long-haul rate.
            </p>
            <p className="text-muted">
              Driver: {money(defaults.driverMnl)} Metro Manila / {money(defaults.driverCrk)} long haul ·
              Helper: {money(defaults.helperMnl)} Metro Manila / {money(defaults.helperCrk)} long haul
            </p>
          </section>
        )}

        {zones && vehicleTypes && (!zones.length || !vehicleTypes.length) && (
          <p className="text-warning">
            Before entering pay lanes, add{' '}
            {!zones.length && <Link to="/app/admin/zones">service zones</Link>}
            {!zones.length && !vehicleTypes.length && ' and '}
            {!vehicleTypes.length && (
              <Link to="/app/admin/settings?tab=vehicle-types">vehicle types</Link>
            )}
            .
          </p>
        )}

        <section className="card-section">
          <div className="row-between section-head">
            <div>
              <h2>Lane pay</h2>
              <p className="text-muted">
                What drivers and helpers earn for each zone-to-zone trip, per vehicle type, plus pay
                for each extra stop. The return direction is set to the same pay automatically.
              </p>
            </div>
            <Button size="sm" onClick={openAddCell} disabled={!zones?.length || !vehicleTypes?.length}>
              Add pay lane
            </Button>
          </div>
          <DataTable
            data={cells ?? []}
            keyField="id"
            dense
            emptyMessage="No pay lanes yet — add driver/helper amounts for each lane."
            columns={[
              {
                key: 'origin',
                header: 'Origin',
                render: (r) => zoneMap[r.originZoneId] ?? '—',
              },
              {
                key: 'dest',
                header: 'Destination',
                render: (r) => zoneMap[r.destinationZoneId] ?? '—',
              },
              {
                key: 'vt',
                header: 'Vehicle type',
                render: (r) => vtMap[r.vehicleTypeId] ?? '—',
              },
              {
                key: 'driver',
                header: 'Driver',
                render: (r) => <span className="data-num">{money(r.driverAmount)}</span>,
              },
              {
                key: 'helper',
                header: 'Helper',
                render: (r) => <span className="data-num">{money(r.helperAmount)}</span>,
              },
              {
                key: 'extra',
                header: 'Extra stop',
                render: (r) => <span className="data-num">{money(r.extraStopAmount)}</span>,
              },
              {
                key: 'actions',
                header: '',
                render: (r) => (
                  <div className="row-actions">
                    <Button size="sm" variant="ghost" onClick={() => openEditCell(r)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteCell(r)}>Delete</Button>
                  </div>
                ),
              },
            ]}
          />
        </section>

        <section className="card-section">
          <div className="row-between section-head">
            <div>
              <h2>CSV import</h2>
              <p className="text-muted">
                Load many pay lanes at once from a spreadsheet using zone codes and vehicle type codes.
                The template lists every zone pair and vehicle type you have — fill in the amounts,
                then check the preview before importing. Unchanged rows are skipped.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={downloadTemplate}>Download template</Button>
          </div>

          <div className="input-group">
            <label className="input-label">CSV file</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="input-field"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFileChosen(file);
              }}
            />
          </div>

          <textarea
            className="csv-textarea"
            placeholder="originZoneCode,destinationZoneCode,vehicleTypeCode,driverAmount,helperAmount,extraStopAmount"
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value);
              setImportPreview(null);
            }}
            rows={5}
          />

          <div className="row" style={{ gap: '0.5rem' }}>
            <Button variant="secondary" loading={importing} onClick={runImportPreview}>Preview</Button>
            <Button
              loading={importing}
              disabled={
                !importPreview ||
                (importPreview.newCount + importPreview.updateCount === 0) ||
                (importPreview.errorCount ?? importPreview.errors?.length ?? 0) > 0
              }
              onClick={confirmImport}
            >
              Confirm import
            </Button>
          </div>

          <FlashMessage flash={importFlash} />

          {importPreview && (
            <>
              {(importPreview.errors?.length ?? 0) > 0 && (
                <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
                  <p style={{ margin: '0 0 0.25rem' }}>Fix these rows in your file, then preview again:</p>
                  {importPreview.errors!.map((e) => (
                    <div key={`${e.row}-${e.field ?? 'row'}`}>
                      Row {e.row}{e.field ? ` (${e.field})` : ''}: {e.message}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-muted">
                {importPreview.totalRows} row{importPreview.totalRows === 1 ? '' : 's'} read — {importPreview.newCount} new,{' '}
                {importPreview.updateCount} updated,{' '}
                {(importPreview.errorCount ?? importPreview.errors?.length ?? 0)} with errors
              </p>
              <DataTable
                dense
                keyField="originZoneCode"
                data={importPreview.diff}
                emptyMessage="No rows parsed from CSV"
                columns={[
                  { key: 'originZoneCode', header: 'Origin' },
                  { key: 'destinationZoneCode', header: 'Destination' },
                  { key: 'vehicleTypeCode', header: 'Vehicle type' },
                  {
                    key: 'driver',
                    header: 'Driver',
                    render: (r) => <span className="data-num">{money(r.proposedDriverAmount)}</span>,
                  },
                  {
                    key: 'helper',
                    header: 'Helper',
                    render: (r) => <span className="data-num">{money(r.proposedHelperAmount)}</span>,
                  },
                  {
                    key: 'change',
                    header: 'Change',
                    render: (r) =>
                      r.change === 'new' ? 'New' : r.change === 'update' ? 'Update' : 'Unchanged',
                  },
                ]}
              />
            </>
          )}
        </section>

        <Modal open={editMeta} onClose={() => setEditMeta(false)} title="Edit pay card">
          <form onSubmit={saveMeta}>
            <Input
              label="Name"
              value={metaForm.name}
              onChange={(e) => setMetaForm({ ...metaForm, name: e.target.value })}
              required
            />
            <Select
              label="Paired rate card"
              value={metaForm.pairedRateCardId}
              onChange={(e) => setMetaForm({ ...metaForm, pairedRateCardId: e.target.value })}
              hint="Optional — links this pay structure to a selling rate card"
            >
              <option value="">None</option>
              {(rateCards ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.name} ({humanize(r.status)})</option>
              ))}
            </Select>
            <h3 style={{ marginTop: 'var(--space-md)' }}>Default pay</h3>
            <Input
              label="Long-haul price threshold (₱)"
              type="number"
              inputMode="decimal"
              step="1"
              min="0"
              value={metaForm.threshold}
              onChange={(e) => setMetaForm({ ...metaForm, threshold: e.target.value })}
              hint="6-wheeler trips priced at or above this pay crew the long-haul (Clark) rate"
            />
            <Select
              label="For 10-wheelers, compare against"
              value={metaForm.basis}
              onChange={(e) => setMetaForm({ ...metaForm, basis: e.target.value as 'own' | '6w' })}
            >
              <option value="6w">The 6-wheeler price for the same lane</option>
              <option value="own">The 10-wheeler's own price</option>
            </Select>
            <div className="grid-2">
              <Input label="Driver — Metro Manila (₱)" type="number" inputMode="decimal" min="0" step="0.01" value={metaForm.driverMnl} onChange={(e) => setMetaForm({ ...metaForm, driverMnl: e.target.value })} />
              <Input label="Driver — long haul (₱)" type="number" inputMode="decimal" min="0" step="0.01" value={metaForm.driverCrk} onChange={(e) => setMetaForm({ ...metaForm, driverCrk: e.target.value })} />
              <Input label="Helper — Metro Manila (₱)" type="number" inputMode="decimal" min="0" step="0.01" value={metaForm.helperMnl} onChange={(e) => setMetaForm({ ...metaForm, helperMnl: e.target.value })} />
              <Input label="Helper — long haul (₱)" type="number" inputMode="decimal" min="0" step="0.01" value={metaForm.helperCrk} onChange={(e) => setMetaForm({ ...metaForm, helperCrk: e.target.value })} />
            </div>
            {flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={busy}>Save</Button>
          </form>
        </Modal>

        <Modal
          open={cellModal}
          onClose={() => setCellModal(false)}
          title={editingCell ? 'Edit pay lane' : 'Add pay lane'}
        >
          <form onSubmit={saveCell}>
            <Select
              label="Origin zone"
              value={cellForm.originZoneId}
              onChange={(e) => setCellForm({ ...cellForm, originZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Select
              label="Destination zone"
              value={cellForm.destinationZoneId}
              onChange={(e) => setCellForm({ ...cellForm, destinationZoneId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(zones ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
            <Select
              label="Vehicle type"
              value={cellForm.vehicleTypeId}
              onChange={(e) => setCellForm({ ...cellForm, vehicleTypeId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {(vehicleTypes ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.code} · {v.name}</option>
              ))}
            </Select>
            <div className="grid-2">
              <Input
                label="Driver amount (₱)"
                type="number"
                step="0.01"
                min="0"
                value={cellForm.driverAmount}
                onChange={(e) => setCellForm({ ...cellForm, driverAmount: e.target.value })}
                required
              />
              <Input
                label="Helper amount (₱)"
                type="number"
                step="0.01"
                min="0"
                value={cellForm.helperAmount}
                onChange={(e) => setCellForm({ ...cellForm, helperAmount: e.target.value })}
                required
              />
            </div>
            <Input
              label="Extra stop amount (₱)"
              type="number"
              step="0.01"
              min="0"
              value={cellForm.extraStopAmount}
              onChange={(e) => setCellForm({ ...cellForm, extraStopAmount: e.target.value })}
            />
            {flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={busy}>{editingCell ? 'Update pay' : 'Save pay'}</Button>
          </form>
        </Modal>

        <ConfirmModal
          open={confirmActivate}
          onClose={() => setConfirmActivate(false)}
          onConfirm={activate}
          title="Activate pay card"
          message={
            (cells ?? []).length === 0
              ? `“${card.name}” has no pay lanes yet, so crew pay will come from the default pay above. Activate anyway? Other active pay cards stay active.`
              : `Activate “${card.name}”? Crew pay on new trips will be worked out from this card. Other active pay cards stay active.`
          }
          confirmLabel="Activate"
          loading={busy}
        />
        <ConfirmModal
          open={confirmDeleteCard}
          onClose={() => setConfirmDeleteCard(false)}
          onConfirm={deleteCard}
          title="Delete pay card"
          message="Delete this draft pay card and all of its pay lanes? This can't be undone."
          confirmLabel="Delete"
          danger
          loading={busy}
        />
        <ConfirmModal
          open={!!confirmDeleteCell}
          onClose={() => setConfirmDeleteCell(null)}
          onConfirm={deleteCell}
          title="Delete pay lane"
          message="Remove this pay lane from the card?"
          confirmLabel="Delete"
          danger
          loading={busy}
        />

        <style>{`
          .back-link { margin-bottom: var(--space-md); }
          .back-link a { color: var(--color-steel); font-size: 0.875rem; }
          .meta-row {
            display: flex;
            align-items: center;
            gap: var(--space-md);
            margin-top: var(--space-sm);
            flex-wrap: wrap;
          }
          .actions-row { display: flex; gap: var(--space-sm); flex-wrap: wrap; }
          .card-section {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            margin-bottom: var(--space-lg);
          }
          .section-head { margin-bottom: var(--space-md); align-items: flex-start; }
          .section-head h2 { margin-bottom: var(--space-xs); }
          .classification h2 { margin-bottom: var(--space-sm); }
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
          .data-num {
            font-family: var(--font-display);
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }
          .csv-textarea {
            width: 100%;
            font-family: var(--font-mono, monospace);
            font-size: 0.8125rem;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: var(--color-surface);
            color: var(--color-text);
            margin-bottom: var(--space-md);
            resize: vertical;
          }
        `}</style>
      </div>
    </Layout>
  );
}
